import Vue from "vue";
import VueRouter from "vue-router";
import { userService, rolesService } from "@/_services";
// router setup
import routes from "./routes";

// plugin setup
Vue.use(VueRouter);

function isExpectedNavigationFailure(error) {
  if (!error) return false;

  const message = typeof error.message === "string" ? error.message : "";
  const name = typeof error.name === "string" ? error.name : "";

  return (
    name === "NavigationDuplicated" ||
    name === "NavigationCancelled" ||
    name === "NavigationAborted" ||
    name === "NavigationRedirected" ||
    /Avoided redundant navigation to current location/i.test(message) ||
    /Redirected when going from/i.test(message) ||
    /Navigation cancelled from/i.test(message) ||
    /Navigation aborted from/i.test(message)
  );
}

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }

  return originalPush.call(this, location).catch((error) => {
    if (isExpectedNavigationFailure(error)) return error;
    throw error;
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject);
  }

  return originalReplace.call(this, location).catch((error) => {
    if (isExpectedNavigationFailure(error)) return error;
    throw error;
  });
};

// configure router
const router = new VueRouter({
  routes, // short for routes: routes
  linkActiveClass: "active",
});

function normalizePath(path) {
  if (!path) return "";

  if (path === "/") return path;

  return path.endsWith("/") ? path.slice(0, -1) : path;
}

function collectMenuPaths(menus) {
  return menus.reduce((paths, menu) => {
    if (menu && menu.key && menu.key != "/franquicia/misDatos") {
      paths.push(normalizePath(menu.key));
    }
    return paths;
  }, []);
}

function isRouteAllowed(routePath, allowedRoutes) {
  const normalizedRoutePath = normalizePath(routePath);

  return allowedRoutes.some((allowedRoute) => {
    if (!allowedRoute) return false;

    return normalizedRoutePath === normalizePath(allowedRoute);
  });
}

// Tras un despliegue, una pestaña abierta (o cacheada) puede pedir trozos de la versión anterior que ya no existen:
// se recarga la página una vez para cargar la versión nueva (como mucho una vez cada 10 s, para no entrar en bucle)
router.onError((error) => {
  if (!/Loading (CSS )?chunk .* failed/i.test((error && error.message) || "")) return;
  try {
    const ultimaRecarga = Number(sessionStorage.getItem("recargaPorVersion") || 0);
    if (Date.now() - ultimaRecarga < 10000) return;
    sessionStorage.setItem("recargaPorVersion", String(Date.now()));
  } catch (e) {
    return; // Sin sessionStorage (algún modo privado) no se recarga: se queda como antes
  }
  window.location.reload();
});

router.beforeEach((to, from, next) => {
  // redirect to login page if not logged in and trying to access a restricted page
  const publicPages = ["/login", "/recover_password", "/restore_login"];
  const authRequired = !publicPages.includes(to.path);
  const user = JSON.parse(localStorage.getItem("user"));
  const storedPagesRoles = JSON.parse(localStorage.getItem("roles-menu"));
  const pagesRoles = Array.isArray(storedPagesRoles) ? storedPagesRoles : [];
  const forbiddenPages = rolesService.getForbiddenPages(pagesRoles, user);

  if (authRequired) {
    if (!user) return next("/login");
    else {
      const user_entity = user;
      if (!userService.esFranquicia(user_entity)) return next("/login");
      if (forbiddenPages.includes(to.path))
        return next("/franquicia/forbidden");

      if (user.extraData.lectura && pagesRoles.length > 0) {
        const allowedRoutes = [
          ...collectMenuPaths(pagesRoles),
          ...["/franquicia/overview"],
        ];

        if (
          to.path !== "/franquicia/forbidden" &&
          !isRouteAllowed(to.path, allowedRoutes)
        ) {
          return next("/franquicia/forbidden");
        }
      }
    }
  }

  next();
});

export { router };
